import { useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImagePicker({ value, onChange, label, accept = "image/*", testid = "image-picker", aspect = "aspect-[4/3]", hint }) {
    const ref = useRef(null);
    const [uploading, setUploading] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Uploaded.");
        } catch (e) {
            console.error(e);
            toast.error("Upload failed.");
        } finally {
            setUploading(false);
            if (ref.current) ref.current.value = "";
        }
    };

    return (
        <div data-testid={testid}>
            {label && <label className="tracked text-coffee/60 text-[10px] block mb-3">{label}</label>}
            <div className="flex flex-col gap-3">
                {value && (
                    <div className={`${aspect} bg-coffee/5 overflow-hidden max-w-xs border border-coffee/10`}>
                        {accept.startsWith("video") ? (
                            <video src={fileUrl(value)} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                        ) : (
                            <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
                        )}
                    </div>
                )}
                <div className="flex gap-2 flex-wrap">
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Paste URL or upload below"
                        className="flex-1 min-w-[200px] bg-transparent border border-coffee/20 px-3 py-2 text-sm text-coffee font-body outline-none focus:border-coffee"
                        data-testid={`${testid}-url`}
                    />
                    <button
                        type="button"
                        onClick={() => ref.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-coffee text-coffee text-xs tracked hover:bg-coffee hover:text-cream transition-colors disabled:opacity-50"
                        data-testid={`${testid}-upload-btn`}
                    >
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {uploading ? "Uploading…" : "Upload"}
                    </button>
                </div>
                <input
                    ref={ref}
                    type="file"
                    accept={accept}
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                    data-testid={`${testid}-input`}
                />
                {hint && (
                    <div className="font-mono text-[10px] text-coffee/50 leading-relaxed">
                        {hint}
                    </div>
                )}
            </div>
        </div>
    );
}
